import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Camera, Upload, MagicWand, X, Stack, PaperPlaneRight, Image as ImageIcon, Robot, User as UserIcon, CheckCircle, Warning, ChatCircleDots, ArrowCounterClockwise } from "@phosphor-icons/react";

function readFileAsDataUrl(file) {
    return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
    });
}

export default function EquipmentFaultFinderPanel({ equipmentId, equipmentName, canRun, user }) {
    const [session, setSession] = useState(null);       // { session_id, status, turns: [] }
    const [pending, setPending] = useState(null);       // { dataUrl, base64, mime } | null
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [history, setHistory] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const fileRef = useRef(null);
    const cameraRef = useRef(null);
    const scrollRef = useRef(null);

    const loadHistory = useCallback(async () => {
        try {
            const res = await api.get(`/equipment/${equipmentId}/fault-chats`);
            setHistory(res.data);
        } catch (err) { /* ignore */ }
    }, [equipmentId]);

    useEffect(() => { loadHistory(); }, [loadHistory]);

    // Auto-scroll to bottom on new turn
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [session?.turns?.length, busy]);

    const onPick = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) {
            toast.error("JPEG, PNG or WEBP only");
            return;
        }
        if (f.size > 8 * 1024 * 1024) { toast.error("Image too large — max 8 MB"); return; }
        const dataUrl = await readFileAsDataUrl(f);
        const base64 = String(dataUrl).split(",")[1] || "";
        setPending({ dataUrl, base64, mime: f.type });
        e.target.value = "";
    };

    const startNewChat = async () => {
        if (busy) return;
        setBusy(true);
        try {
            const payload = {
                initial_message: input.trim(),
                image_base64: pending?.base64 || undefined,
                mime_type: pending?.mime || undefined,
            };
            const res = await api.post(`/equipment/${equipmentId}/fault-chat`, payload);
            setSession(res.data);
            setInput("");
            setPending(null);
            loadHistory();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const sendReply = async () => {
        if (busy) return;
        if (!input.trim() && !pending) { toast.error("Type a reply or attach a photo"); return; }
        setBusy(true);
        try {
            const payload = {
                message: input.trim() || "(see attached photo)",
                image_base64: pending?.base64 || undefined,
                mime_type: pending?.mime || undefined,
            };
            const res = await api.post(`/equipment/fault-chat/${session.session_id}/reply`, payload);
            setSession(res.data);
            setInput("");
            setPending(null);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const reset = () => { setSession(null); setInput(""); setPending(null); };

    const openPastChat = async (item) => {
        try {
            const res = await api.get(`/equipment/fault-chats/${item.id}`);
            // Reformat into session shape; can't continue if closed
            setSession({
                session_id: res.data.id,
                status: res.data.status,
                turns: res.data.turns,
                readonly: res.data.status !== "asking",
            });
            setShowHistory(false);
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const isClosed = session && (session.status === "resolved" || session.status === "escalate" || session.readonly);

    return (
        <div className="p-4 sm:p-8 space-y-4" data-testid="equipment-faultfinder-panel">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-cyan-300 font-display font-bold tracking-[0.18em] text-sm uppercase flex items-center gap-2"><MagicWand size={14} weight="bold" /> AI Fault Finding Assistant</div>
                    <div className="text-xs text-slate-400">Conversational diagnosis for {equipmentName}. The assistant asks one question at a time — answer naturally.</div>
                </div>
                <button onClick={() => { setShowHistory((s) => !s); loadHistory(); }} className="bg-white/10 hover:bg-white/15 text-cyan-100 text-xs uppercase tracking-wide px-3 py-1.5 rounded-sm inline-flex items-center gap-1.5" data-testid="faultchat-history-btn">
                    <Stack size={13} weight="bold" /> Past chats ({history.length})
                </button>
            </div>

            {!canRun && (
                <div className="rounded-md border border-amber-400/30 bg-amber-400/5 p-4 text-xs text-amber-200">
                    AI Fault Finder is reserved for electricians, planners and admins. View-only access for your role.
                </div>
            )}

            {/* Chat thread */}
            <div ref={scrollRef} className="rounded-md border border-cyan-400/20 bg-cyan-400/5 max-h-[440px] overflow-y-auto" data-testid="faultchat-thread">
                {!session ? (
                    <div className="p-6 text-center">
                        <Robot size={36} weight="duotone" className="text-cyan-300 mx-auto mb-2" />
                        <div className="text-white font-bold text-base">Start a new fault-finding chat</div>
                        <div className="text-xs text-slate-300 mt-1">Tell the assistant what's wrong, optionally attach a control-screen photo. It will walk you through the diagnosis step by step.</div>
                    </div>
                ) : (
                    <div className="p-3 sm:p-4 space-y-3">
                        {session.turns.map((t, i) => (
                            <ChatBubble key={i} turn={t} userName={session.user_name || user?.name} />
                        ))}
                        {busy && (
                            <ChatBubble turn={{ role: "assistant", content: "thinking…" }} typing />
                        )}
                        {isClosed && (
                            <div className={`rounded-sm p-2.5 text-center text-xs uppercase tracking-wider font-bold ${session.status === "resolved" ? "bg-green-500/20 border border-green-500/40 text-green-200" : "bg-amber-500/20 border border-amber-500/40 text-amber-200"}`}>
                                {session.status === "resolved" ? (<><CheckCircle size={13} weight="bold" className="inline mb-0.5 mr-1" /> Fault resolved · chat closed</>) : (<><Warning size={13} weight="bold" className="inline mb-0.5 mr-1" /> Escalated to Shift Millwright</>)}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Composer */}
            {canRun && !isClosed && (
                <div className="rounded-md border border-cyan-400/20 bg-slate-950/40 p-3 space-y-2">
                    {pending && (
                        <div className="flex items-center gap-2">
                            <img src={pending.dataUrl} alt="attach" className="h-16 rounded border border-cyan-400/30 bg-black" />
                            <button onClick={() => setPending(null)} className="text-red-300 text-xs underline" data-testid="remove-attachment">Remove attachment</button>
                        </div>
                    )}
                    <textarea
                        rows={2}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); session ? sendReply() : startNewChat(); }
                        }}
                        placeholder={session ? "Your reply… (Enter to send · Shift+Enter for new line)" : "Describe the problem… e.g. 'The Tippler does not want to tip'"}
                        className="w-full bg-white/5 text-white placeholder-slate-400 text-sm rounded-sm px-3 py-2 outline-none border border-cyan-400/20 focus:border-cyan-400 resize-none"
                        data-testid="faultchat-input"
                    />
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-1.5">
                            <button onClick={() => cameraRef.current?.click()} title="Take photo" className="p-2 rounded bg-white/10 hover:bg-white/20 text-cyan-100" data-testid="faultchat-camera-btn"><Camera size={14} weight="bold" /></button>
                            <button onClick={() => fileRef.current?.click()} title="Upload from gallery" className="p-2 rounded bg-white/10 hover:bg-white/20 text-cyan-100" data-testid="faultchat-upload-btn"><Upload size={14} weight="bold" /></button>
                            {session && (
                                <button onClick={reset} title="Start a new chat" className="p-2 rounded bg-white/10 hover:bg-white/20 text-cyan-100" data-testid="faultchat-new-btn"><ArrowCounterClockwise size={14} weight="bold" /></button>
                            )}
                            <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={onPick} className="hidden" data-testid="faultchat-camera-input" />
                            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={onPick} className="hidden" data-testid="faultchat-file-input" />
                        </div>
                        <button
                            onClick={() => (session ? sendReply() : startNewChat())}
                            disabled={busy || (!input.trim() && !pending)}
                            className="bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-bold uppercase tracking-wide text-xs px-3 py-2 rounded-sm inline-flex items-center gap-1.5 disabled:opacity-50"
                            data-testid="faultchat-send-btn"
                        >
                            <PaperPlaneRight size={14} weight="bold" /> {busy ? "Sending…" : session ? "Reply" : "Start chat"}
                        </button>
                    </div>
                </div>
            )}

            {canRun && isClosed && (
                <button onClick={reset} className="bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-bold uppercase tracking-wide text-xs px-3 py-2 rounded-sm inline-flex items-center gap-2" data-testid="faultchat-new-after-close">
                    <ArrowCounterClockwise size={14} weight="bold" /> Start a new chat
                </button>
            )}

            {/* History drawer */}
            {showHistory && (
                <div className="rounded-md border border-cyan-400/20 bg-cyan-400/5 p-3" data-testid="faultchat-history-list">
                    <div className="text-cyan-300 font-display font-bold tracking-[0.18em] text-xs uppercase mb-2 flex items-center gap-2"><Stack size={12} weight="bold" /> Past fault-finding chats</div>
                    {history.length === 0 ? (
                        <div className="text-slate-400 text-xs italic">No previous chats on this equipment.</div>
                    ) : (
                        <div className="space-y-1.5">
                            {history.map((h) => (
                                <button key={h.id} onClick={() => openPastChat(h)} className="w-full text-left rounded-md p-2.5 bg-white/5 hover:bg-white/10 border border-white/10 flex items-start gap-3" data-testid={`faultchat-history-${h.id}`}>
                                    <StatusDot status={h.status} />
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm text-slate-100 line-clamp-2">{h.summary}</div>
                                        <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-0.5">{h.user_name} · {new Date(h.updated_at).toLocaleString()} · {h.turns?.length || 0} turns</div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function StatusDot({ status }) {
    const map = {
        resolved: { color: "bg-green-400", label: "RES" },
        escalate: { color: "bg-amber-400", label: "ESC" },
        asking: { color: "bg-cyan-400 animate-pulse", label: "OPEN" },
    };
    const s = map[status] || map.asking;
    return (
        <div className="flex flex-col items-center gap-0.5 mt-1">
            <span className={`w-2.5 h-2.5 rounded-full ${s.color}`} />
            <span className="text-[8px] uppercase tracking-wider text-slate-400 font-bold">{s.label}</span>
        </div>
    );
}

function ChatBubble({ turn, typing, userName }) {
    const isAi = turn.role === "assistant";
    return (
        <div className={`flex items-start gap-2 ${isAi ? "" : "flex-row-reverse"}`}>
            <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${isAi ? "bg-cyan-500/30 text-cyan-200" : "bg-white/10 text-slate-200"}`}>
                {isAi ? <Robot size={14} weight="duotone" /> : <UserIcon size={14} weight="bold" />}
            </div>
            <div className={`max-w-[78%] rounded-md px-3 py-2 ${isAi ? "bg-slate-900/70 border border-cyan-400/20 text-slate-100" : "bg-cyan-400/15 border border-cyan-400/30 text-cyan-50"}`}>
                {turn.has_image && !typing && (
                    <div className="text-[10px] uppercase tracking-wider text-cyan-200/70 mb-1 flex items-center gap-1"><ImageIcon size={10} weight="bold" /> photo attached</div>
                )}
                <div className={`whitespace-pre-wrap text-sm leading-snug ${typing ? "animate-pulse italic text-slate-300" : ""}`}>{turn.content}</div>
                {!typing && (
                    <div className="text-[9px] uppercase tracking-wider text-slate-500 mt-1 flex items-center gap-1">
                        {isAi ? "AI Assistant" : (userName || "You")}{turn.created_at ? ` · ${new Date(turn.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : ""}
                    </div>
                )}
            </div>
        </div>
    );
}
